import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import MarkerCache from '../utils/markerCache';

/**
 * Custom hook for loading markers with viewport-based queries and caching
 *
 * @returns {Object} { markers, gridData, isLoading, error, loadMarkers, clearCache }
 */
export const useMarkerLoader = () => {
  const [markers, setMarkers] = useState([]);
  const [gridData, setGridData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadedMarkerCount, setLoadedMarkerCount] = useState(0);

  // Marker cache instance
  const markerCache = useRef(new MarkerCache(2000));

  // Request management
  const abortControllerRef = useRef(null);
  const currentRequestRef = useRef(null);

  /**
   * Load markers for a given viewport and zoom level
   *
   * @param {Object} viewport - { neLat, neLng, swLat, swLng }
   * @param {number} zoom - Current zoom level (1-20)
   * @param {boolean} forceReload - Skip cache and force API call
   */
  const loadMarkers = useCallback(async (viewport, zoom, forceReload = false) => {
    if (!viewport || zoom === null) {
      console.warn('[useMarkerLoader] Invalid viewport or zoom');
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Check cache first (skip if forceReload is true)
    if (!forceReload && markerCache.current.isViewportLoaded(viewport, zoom)) {
      const cachedMarkers = markerCache.current.getMarkersInViewport(viewport);
      setMarkers(cachedMarkers);
      return;
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    // Create request ID to handle race conditions
    const requestId = Date.now();
    currentRequestRef.current = requestId;

    setIsLoading(true);
    setError(null);
    setLoadedMarkerCount(0);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/fetchData/markerScanViewport`,
        {
          ...viewport,
          zoom,
          page: 1,
          limit: calculatePageLimit(zoom)
        },
        {
          signal: abortControllerRef.current.signal
        }
      );

      // Check if this is still the current request (not cancelled/superseded)
      if (currentRequestRef.current !== requestId) {
        console.log('[useMarkerLoader] Ignoring outdated response');
        return;
      }

      if (response.data) {
        const { markers: newMarkers, grid: newGrid } = response.data;
        setLoadedMarkerCount(newMarkers.length);

        console.log(`[useMarkerLoader] Loaded ${newMarkers.length} markers, zoom: ${zoom}`);

        // Update cache
        markerCache.current.addMarkers(newMarkers);
        markerCache.current.addGridData(newGrid);
        markerCache.current.recordViewport(viewport, zoom);

        // Get all markers in current viewport (cached + new)
        const allMarkersInView = markerCache.current.getMarkersInViewport(viewport);

        setMarkers(allMarkersInView);
        setGridData(newGrid);

        // Log cache stats
        console.log('[MarkerCache Stats]', markerCache.current.getStats());
      }
    } catch (err) {
      // Ignore abort errors (they're expected when cancelling requests)
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error('[useMarkerLoader] Error loading markers:', err);
        setError(err);
      }
    } finally {
      if (currentRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, []);

  /**
   * Clear the marker cache
   * Useful when filters change or user needs fresh data
   */
  const clearCache = useCallback(() => {
    markerCache.current.clear();
    setMarkers([]);
    setGridData([]);
    console.log('[useMarkerLoader] Cache cleared');
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    markers,
    gridData,
    isLoading,
    error,
    loadMarkers,
    clearCache,
    loadedMarkerCount
  };
};

/**
 * Helper: Calculate page limit based on zoom level
 * Matches backend zoom-based filtering strategy
 */
function calculatePageLimit(zoom) {
  if (zoom <= 8) return 120;   // Very low density (3x)
  if (zoom <= 12) return 300;  // Low density (3x)
  if (zoom <= 15) return 600; // Medium density (3x)
  return 900;                  // High density (3x)
}

export default useMarkerLoader;
